import { LoggedOutAppBarItems } from '#app/components/AppBar/components/LoggedOutAppBarItems.js';
import { useSidebar } from '#app/contexts/SidebarContext/useSidebar.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import {
  BannerContainer,
  BannerContent,
  BannerLayout,
  Nav,
} from './AppBar.styles.js';
import { AppBarLeft, AppBarLeftProps } from './components/AppBarLeft.js';
import { NavbarSpacer } from './components/NavbarSpacer.js';
import { lazy, type ReactNode, Suspense } from 'react';

const AuthenticatedAppBar = lazy(async () => {
  return {
    default: (
      await import('#app/components/AppBar/components/AuthenticatedAppBar.js')
    ).AuthenticatedAppBar,
  };
});

export type AppBarProps = {
  /**
   * @param {ReactNode} banner - The contextual banner to be displayed on the page
   */
  readonly banner?: ReactNode;
  readonly hideAppBarBorderAtMd?: boolean;
  readonly isContractor?: boolean;
  /**
   * @param {ReactNode} banner - Promotional banner for launches, surveys, and the like. `banner` always takes precedence
   */
  readonly promotionalBanner?: ReactNode;
  readonly routeLabelOverride?: AppBarLeftProps['routeLabelOverride'];
  readonly username?: string;
};

export const AppBar = ({
  banner,
  hideAppBarBorderAtMd,
  isContractor,
  promotionalBanner,
  routeLabelOverride,
  username,
}: AppBarProps) => {
  const currentUser = useCurrentUser();
  const isLoggedIn = Boolean(currentUser);
  const sidebar = useSidebar();

  return (
    <Nav $hideAppBarBorderAtMd={hideAppBarBorderAtMd}>
      {banner ? (
        <BannerContainer>
          <BannerLayout>
            {isLoggedIn ? (
              <NavbarSpacer
                open={
                  (sidebar.desktop?.isExpanded || sidebar.mobile?.isOpen) ??
                  false
                }
              />
            ) : null}
            <BannerContent>{banner}</BannerContent>
          </BannerLayout>
        </BannerContainer>
      ) : promotionalBanner ? (
        promotionalBanner
      ) : null}

      <Nav.Layout>
        <AppBarLeft
          routeLabelOverride={routeLabelOverride}
          username={username}
        />

        <Nav.Right>
          {isLoggedIn ? (
            <Suspense>
              <AuthenticatedAppBar isContractor={isContractor} />
            </Suspense>
          ) : (
            <LoggedOutAppBarItems />
          )}
        </Nav.Right>
      </Nav.Layout>
    </Nav>
  );
};
